import { apiDelete, apiGet, apiPatch, apiPost } from './client';

export const ZAPIER_EVENT_OPTIONS = [
  'contact.created',
  'lead.created',
  'deal.created',
  'task.created',
  'email.logged',
] as const;

export type ZapierEventType = (typeof ZAPIER_EVENT_OPTIONS)[number];

export type ZapierHook = {
  id: string;
  eventType: ZapierEventType;
  targetUrl: string;
  status: string;
  createdAt: number;
  lastSuccessAt: number | null;
  lastErrorAt: number | null;
  errorMessage: string | null;
  secretPreview?: string;
};

export type ZapierHookCreateResponse = {
  hook: ZapierHook & { secret: string };
};

export async function listZapierHooks() {
  return apiGet<{
    hooks: ZapierHook[];
    availableEvents: ZapierEventType[];
  }>('/integrations/zapier/hooks');
}

export async function createZapierHook(payload: { eventType: ZapierEventType; targetUrl: string }) {
  return apiPost<ZapierHookCreateResponse, typeof payload>('/integrations/zapier/hooks', payload);
}

export async function deleteZapierHook(id: string) {
  return apiDelete<{ message: string }>(`/integrations/zapier/hooks/${id}`);
}

export async function testZapierHook(id: string) {
  return apiPost<{ message: string }>(`/integrations/zapier/hooks/${id}/test`);
}

export type GoogleIntegrationStatus = {
  connected: boolean;
  requiresUpgrade?: boolean;
  email?: string;
  syncContacts?: boolean;
  lastContactsSyncAt?: number | null;
  requiresReauthForContacts?: boolean;
  canScheduleMeetings?: boolean;
  requiresReauthForMeetings?: boolean;
};

export async function getGoogleWorkspaceStatus() {
  return apiGet<GoogleIntegrationStatus>('/integrations/google/status');
}

export async function createGoogleOAuthUrl() {
  return apiPost<{ url: string; state: string }>('/integrations/google/oauth-url');
}

export async function syncGoogleWorkspace() {
  return apiPost<{ message: string; created: number; updated: number }>('/integrations/google/sync');
}

export async function disconnectGoogleWorkspace() {
  return apiDelete<{ message: string }>('/integrations/google');
}

export async function updateGooglePreferences(payload: { syncContacts?: boolean }) {
  return apiPatch<{ message: string; syncContacts: boolean }, typeof payload>(
    '/integrations/google/preferences',
    payload,
  );
}

export type ScheduleMeetingPayload = {
  taskId: string;
  start: string;
  end?: string;
  durationMinutes?: number;
  attendees?: string[];
  title?: string;
  notes?: string;
  location?: string;
};

export async function scheduleGoogleCalendarMeeting(payload: ScheduleMeetingPayload) {
  return apiPost<{
    message: string;
    eventId: string | null;
    htmlLink: string | null;
    start: string;
    end: string;
  }, ScheduleMeetingPayload>('/integrations/google/calendar/events', payload);
}
